import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Route, MapPin } from "lucide-react";
import { waLink, tourMessage, trackWhatsApp } from "@/lib/whatsapp";
import { IMAGES } from "@/lib/images";
import { formatBRL } from "@/lib/tours";
import WhatsAppIcon from "@/components/landing/WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];
const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

export default function TourHero({ tour }) {
  return (
    <header className="relative flex min-h-[85vh] items-end overflow-hidden">
      <motion.img
        key={tour.slug}
        src={IMAGES[tour.image]}
        alt={tour.name}
        initial={{ scale: 1.12, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: EASE }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1117] via-[#0D1117]/55 to-[#0D1117]/25" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pb-24">
        <motion.div {...fade(0.1)}>
          <Link
            to="/#passeios"
            data-testid="tour-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-300 transition-colors duration-300 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Todos os passeios
          </Link>
        </motion.div>

        <motion.p
          {...fade(0.25)}
          className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-300/90"
        >
          <Route className="h-3.5 w-3.5 shrink-0" />
          {tour.stops.join(" → ")}
        </motion.p>

        <motion.h1
          {...fade(0.35)}
          data-testid="tour-page-title"
          className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl"
        >
          {tour.name}
        </motion.h1>

        <motion.div {...fade(0.5)} className="mt-8 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 rounded-full border border-white/15 bg-[#0D1117]/60 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-200 backdrop-blur-md">
            <Clock className="h-3.5 w-3.5 text-amber-400" />
            {tour.duration}
          </span>
          <span className="flex items-center gap-2 rounded-full border border-white/15 bg-[#0D1117]/60 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-200 backdrop-blur-md">
            <MapPin className="h-3.5 w-3.5 text-amber-400" />
            {tour.stops.length} paradas
          </span>
          <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-300 backdrop-blur-md">
            A partir de R$ {formatBRL(tour.price)} / pessoa
          </span>
        </motion.div>

        <motion.div {...fade(0.65)} className="mt-10">
          <a
            href={waLink(tourMessage(tour.name))}
            onClick={() => trackWhatsApp("pagina_passeio_hero", tour.name)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tour-page-cta-hero"
            className="inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_0_40px_rgba(37,211,102,0.35)] transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-[#20BD5A]"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Consultar e reservar
          </a>
        </motion.div>
      </div>
    </header>
  );
}
